import CustomComponent from '../CustomComponent';
import Request, { ApiMode } from '../../utils/request';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';

const DEFAULT_CONFIRM_ENDPOINT = '/api/venueRegistration/confirmSection/submit';
const SUBMIT_REGISTRATION_CLASS = 'js-submit-registration';
const CONFIRM_BTN_SELECTOR = '.lta-confirm-section__btn';

export class ConfirmSectionComponent extends CustomComponent {
	static key = 'lta-confirm-section';

	static get observedAttributes() {
		return ['title', 'description', 'disabled', 'classes', 'button-label', 'section', 'confirm-section-endpoint'];
	}

	onLoad() {
		if (this._clickBound) {
			return;
		}

		this._clickBound = true;
		this.addEventListener('click', (event) => this.handleConfirmClick(event));
	}

	/**
	 * Submit registration uses the same button class but is handled by Landing/index.js.
	 * Skip the shared confirm-section POST for that flow.
	 */
	shouldHandleConfirmClick() {
		const { classes = '', disabled = 'false' } = this.props;
		const isDisabled = disabled === 'true' || disabled === '';
		const isSubmitRegistration = classes.split(/\s+/).includes(SUBMIT_REGISTRATION_CLASS);

		return !isDisabled && !isSubmitRegistration;
	}

	async handleConfirmClick(event) {
		const button = event.target?.closest?.(CONFIRM_BTN_SELECTOR);
		if (!button || !this.contains(button)) {
			return;
		}

		if (!this.shouldHandleConfirmClick()) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		if (button.disabled || button.getAttribute('aria-busy') === 'true') {
			return;
		}

		// confirm-section-endpoint → props.confirmSectionEndpoint
		const endpoint = this.props.confirmSectionEndpoint || DEFAULT_CONFIRM_ENDPOINT;
		if (!endpoint) {
			return;
		}

		button.setAttribute('aria-busy', 'true');
		button.disabled = true;
		startLoadingAnimation();

		try {
			const { section } = this.props;
			const response = await Request.post(
				endpoint,
				{
					...(section ? { section } : {}),
					confirmed: true
				},
				{
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);

			const { Success, success, errorMessage, redirectUrl, redirectURL } = response || {};
			const nextUrl = redirectUrl || redirectURL;

			stopLoadingAnimation();

			if (errorMessage) {
				// eslint-disable-next-line no-console
				console.error('ConfirmSection – confirm error:', errorMessage);
				button.removeAttribute('aria-busy');
				button.disabled = false;
				return;
			}

			if (Success || success) {
				if (nextUrl) {
					this.goToUrl(nextUrl);
					return;
				}

				window.location.reload();
				return;
			}

			button.removeAttribute('aria-busy');
			button.disabled = false;
		} catch (error) {
			stopLoadingAnimation();
			button.removeAttribute('aria-busy');
			button.disabled = false;
			// eslint-disable-next-line no-console
			console.error('ConfirmSection – confirm error:', error?.message || error);
		}
	}

	goToUrl(redirectUrl) {
		if (!redirectUrl) {
			return;
		}

		if (/^https?:\/\//i.test(redirectUrl)) {
			window.location.href = redirectUrl;
			return;
		}

		window.location.href = `${window.location.origin}${redirectUrl}`;
	}

	render() {
		const {
			title = 'Confirm this section',
			description = 'Confirm your details to continue your registration.',
			disabled = 'false',
			classes = '',
			buttonLabel = 'Confirm section details'
		} = this.props;

		const isDisabled = disabled === 'true' || disabled === '';
		const classNames = ['lta-confirm-section', classes].filter(Boolean).join(' ');

		return `
			<div class="${classNames}">
				<h2 class="lta-confirm-section__title">${title}</h2>
				${description ? `<p class="lta-confirm-section__description">${description}</p>` : ''}
				<div class="lta-confirm-section__actions">
					<button
						type="button"
						class="lta-btn lta-btn--lg lta-btn--primary lta-confirm-section__btn"
						${isDisabled ? 'disabled' : ''}
					>
						${buttonLabel}
					</button>
				</div>
			</div>
		`;
	}
}

export default ConfirmSectionComponent;
